import { useCallback, useState } from 'react';
import { CheckCircle2, FileType2, Loader2, RefreshCw, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui-kit/alert-dialog';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { useToast } from '@/hooks/use-toast';
import { useGetPreSignedUrlForUpload } from '@/lib/api/hooks/use-storage';
import { ModuleName } from '@/constant/modules.constants';
import graphqlClient from '@/graphql/graphql-client';
import {
  INSERT_TEMPLATE_DOCX_MUTATION,
  UPDATE_TEMPLATE_DOCX_MUTATION,
} from '@/graphql/mutations/templates';
import { useDocxBuilder } from './context';

const MAX_FILE_SIZE_MB = 8;
const projectKey = import.meta.env.VITE_X_BLOCKS_KEY || '';

function formatBytes(bytes: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocxBuilderStepUpload() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { templateId, docx, isDocxLoading, replacing, savedCount, setReplacing, onDocxSaved } =
    useDocxBuilder();
  const { mutateAsync: getPreSignedUrl } = useGetPreSignedUrlForUpload();

  const [phase, setPhase] = useState<'idle' | 'uploading' | 'saving'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const busy = phase !== 'idle';
  const showDropzone = (!docx && !isDocxLoading) || replacing;

  const handleFile = useCallback(
    async (file: File) => {
      if (!file.name.toLowerCase().endsWith('.docx')) {
        setError(
          t('DOCX_BUILDER_UPLOAD_ERROR_TYPE', { defaultValue: 'Only .docx files are supported.' })
        );
        return;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setError(
          t('DOCX_BUILDER_UPLOAD_ERROR_SIZE', {
            defaultValue: `File is too large. Maximum size is ${MAX_FILE_SIZE_MB} MB.`,
          })
        );
        return;
      }

      setError(null);
      setPhase('uploading');

      try {
        const presigned = await getPreSignedUrl({
          name: file.name,
          projectKey,
          itemId: '',
          metaData: '',
          accessModifier: 'Public',
          configurationName: 'Default',
          parentDirectoryId: '',
          tags: '',
          moduleName: ModuleName.DefaultConstruct,
        });

        if (!presigned.isSuccess || !presigned.uploadUrl) {
          throw new Error('Failed to get upload URL from storage.');
        }

        await fetch(presigned.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type':
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'x-ms-blob-type': 'BlockBlob',
          },
        });

        setPhase('saving');

        const originalUrl = presigned.uploadUrl.split('?')[0];
        const originalKey = presigned.fileId || '';

        if (docx?.id && replacing) {
          await graphqlClient.request(UPDATE_TEMPLATE_DOCX_MUTATION, {
            ItemId: docx.id,
            OriginalUrl: originalUrl,
            OriginalKey: originalKey,
            OriginalName: file.name,
            SizeBytes: file.size,
            Status: 'uploaded',
          });
        } else {
          await graphqlClient.request(INSERT_TEMPLATE_DOCX_MUTATION, {
            TemplateId: templateId,
            OriginalUrl: originalUrl,
            OriginalKey: originalKey,
            OriginalName: file.name,
            SizeBytes: file.size,
            Status: 'uploaded',
          });
        }

        toast({
          variant: 'success',
          title: t('DOCX_BUILDER_UPLOAD_SUCCESS', { defaultValue: 'Document uploaded' }),
        });
        onDocxSaved();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Something went wrong';
        setError(message);
        toast({ variant: 'destructive', title: message });
      } finally {
        setPhase('idle');
      }
    },
    [docx, replacing, templateId, getPreSignedUrl, onDocxSaved, toast, t]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      e.target.value = '';
    },
    [handleFile]
  );

  return (
    <div className="space-y-4">
      {error && <div className="p-3 rounded-md bg-crimson/10 text-crimson text-sm">{error}</div>}

      {isDocxLoading && !busy ? (
        <div className="flex items-center gap-3 rounded-lg border border-border p-4 animate-pulse">
          <div className="h-10 w-10 rounded-lg bg-muted shrink-0" />
          <div className="flex-1 space-y-2 min-w-0">
            <div className="h-4 w-48 rounded bg-muted" />
            <div className="h-3 w-16 rounded bg-muted" />
          </div>
        </div>
      ) : busy ? (
        <div className="border-2 border-selise-blue/30 rounded-lg p-10 bg-selise-blue/5 flex flex-col items-center gap-3">
          <Loader2 className="w-10 h-10 text-selise-blue animate-spin" />
          <p className="text-sm text-muted-foreground">
            {phase === 'uploading'
              ? t('DOCX_BUILDER_UPLOAD_UPLOADING', { defaultValue: 'Uploading document…' })
              : t('DOCX_BUILDER_UPLOAD_SAVING', { defaultValue: 'Saving…' })}
          </p>
        </div>
      ) : showDropzone ? (
        <>
          <div
            className="flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-border p-8 transition-colors hover:border-selise-blue/50 hover:bg-selise-blue/5 cursor-pointer"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => document.getElementById('docx-builder-file-input')?.click()}
          >
            <Upload className="h-12 w-12 text-selise-blue" />
            <div className="text-center">
              <p className="font-medium text-[hsl(var(--fg))]">
                {t('DOCX_BUILDER_UPLOAD_DROPZONE_LABEL', {
                  defaultValue: 'Drop your Word document here or click to browse',
                })}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {t('DOCX_BUILDER_UPLOAD_DROPZONE_HINT', {
                  defaultValue: `Word .docx (max ${MAX_FILE_SIZE_MB} MB)`,
                })}
              </p>
            </div>
            <input
              id="docx-builder-file-input"
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={handleInputChange}
            />
          </div>
          {replacing && (
            <Button variant="ghost" size="sm" onClick={() => setReplacing(false)}>
              Cancel
            </Button>
          )}
        </>
      ) : (
        <div className="flex items-center justify-between rounded-lg border border-border p-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-lg bg-selise-blue/10 flex items-center justify-center shrink-0">
              <FileType2 className="h-5 w-5 text-selise-blue" />
            </div>
            <div className="min-w-0">
              <p className="font-medium truncate">{docx?.originalName}</p>
              <p className="text-xs text-globe-grey">{formatBytes(docx?.sizeBytes ?? 0)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant="secondary" className="gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Uploaded
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1"
              onClick={() => {
                if (savedCount > 0) {
                  setConfirmOpen(true);
                } else {
                  setReplacing(true);
                }
              }}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Replace
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace the document?</AlertDialogTitle>
            <AlertDialogDescription>
              Replacing the file clears the {savedCount} field{savedCount === 1 ? '' : 's'}{' '}
              you&apos;ve marked. You&apos;ll mark fields again on the new document.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmOpen(false);
                setReplacing(true);
              }}
            >
              Replace document
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
